/**
 * FloorBoundaryTracer — native in-portal floor-plan tracing + grid overlay.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Stage 1 of the grid-first intake (owner lock 2026-06-07). Replaces the
 *  blind entrance-pada typing with: upload a floor plan → trace the boundary
 *  → the grid generates on the plan (read-only preview). Later stages add
 *  click-on-grid entrances (pada derived) + AUO (zone derived).
 *
 *  COORDINATE SPACE (OQ-C): the boundary is captured in the IMAGE'S NATURAL
 *  PIXEL space. The SVG `viewBox` is set to `0 0 width height` (the image's
 *  natural size), so a click anywhere maps — via the SVG CTM — straight to
 *  image pixels. That pixel polygon feeds `computeGrid` with NO transform
 *  (verified scale/translate-invariant). One math, on-screen == report.
 *
 *  LAYERS (z-order): image <img> as background → SVG overlay on top carrying
 *  (a) the read-only grid preview (zone wedges + 32 rays) once a boundary is
 *  closed, (b) the traced polygon + draggable vertex handles. SVG (not canvas)
 *  because the overlay is vector, needs crisp hit-testing for vertex drag, and
 *  scales losslessly with the responsive image.
 *
 *  When there is NO uploaded image yet, the tracer still works on a neutral
 *  canvas sized to a default pixel box — so a boundary can be traced even
 *  before the storage rule is deployed (the image is the only part gated).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Btn3 } from './ui';
import {
  computeGrid,
  isValidBoundary,
  pointInPolygon,
  shoelaceCentroid,
  rectCorners,
  ENTRANCE_LABELS,
  ZONE_LABELS,
  type Pt,
  type GridResult,
} from '../lib/vastuGrid';
import {
  cleanBoundary,
  overlayMetrics,
  zoneStrip,
  zoneDividerDegs,
  zoneOfAngle,
  radialLabel,
  dividerTick,
  rayDir,
} from '../lib/gridOverlay';
import {
  ZONE_OVERLAY_COLOURS,
  zoneColour,
  padaColour,
} from '../lib/padaColours';
import {
  imageFloorPlanSources,
  FLOORPLAN_UPLOAD_ACCEPT,
  type FloorPlan,
  type FloorPlanSource,
} from '../lib/floorPlan';
import {
  PERIMETER_ENTRANCE_TYPES,
  INTERNAL_ENTRANCE_TYPES,
  type EntranceMark,
} from '../lib/entranceMarks';
import { doorSpan, nearestEdge } from '../lib/doorGeometry';
import {
  makeMarkup,
  MARKUP_TYPES,
  MARKUP_LABEL,
  isRectMarkup,
  type EvalMarkup,
  type MarkupType,
} from '../lib/evalMarkups';
import { regionForMark, type AuoMark } from '../lib/auoMarks';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ZoomPanControls from './evaluate/ZoomPanControls';

/** The three click modes — kept mutually exclusive so a trace click, an
 *  entrance click and an AUO click never interfere. */
export type TracerMode = 'trace' | 'entrance' | 'auo' | 'markup';

/** Fallback drawing surface size when no image has been uploaded yet. */
const FALLBACK_W = 800;
const FALLBACK_H = 600;

/** PURE — an SVG path for the architectural REVISION CLOUD (a scalloped rectangle):
 *  semicircular bumps bulge outward along each side. `r` ≈ bump radius. */
function scallopRectPath(x: number, y: number, w: number, h: number, r: number): string {
  const side = (x1: number, y1: number, x2: number, y2: number): string => {
    const len = Math.hypot(x2 - x1, y2 - y1);
    const n = Math.max(2, Math.round(len / (2 * r)));
    const ux = (x2 - x1) / n, uy = (y2 - y1) / n;
    const rr = Math.hypot(ux, uy) / 2;
    let s = '';
    for (let i = 0; i < n; i++) s += ` a ${rr.toFixed(1)} ${rr.toFixed(1)} 0 0 1 ${ux.toFixed(1)} ${uy.toFixed(1)}`;
    return s;
  };
  return `M ${x} ${y}` + side(x, y, x + w, y) + side(x + w, y, x + w, y + h) + side(x + w, y + h, x, y + h) + side(x, y + h, x, y) + ' Z';
}


export default function FloorBoundaryTracer({
  floorplan,
  boundary,
  north,
  onUpload,
  onPickProjectFile,
  // onClearPlan retained in the props type (caller passes it) but no longer
  // surfaced — "Remove plan" was deleted (#8). Not destructured → not unused.
  onEditPlan,
  onChangeBoundary,
  uploading,
  projectImageFiles = [],
  entranceMarks = [],
  onAddEntrance,
  onAddInternalEntrance,
  onMoveEntrance,
  onSetEntranceEnd,
  onRemoveEntrance,
  onSetEntranceType,
  onSetEntranceName,
  onSetPrincipal,
  auoMarks = [],
  onAddAuo,
  onMoveAuo,
  onRemoveAuo,
  onResizeAuo,
  onResizeAuoAnchored,
  selectedAuoId = null,
  onSelectAuo,
  markups,
  onAddMarkup,
  onMoveMarkup,
  onResizeMarkup,
  onRemoveMarkup,
  selectedMarkupId = null,
  onSelectMarkup,
  readOnlyBoundary = false,
  readOnlyMarks = false,
  enableZoom = false,
}: {
  floorplan: FloorPlan | null | undefined;
  boundary: Pt[] | null | undefined;
  /** true-north rotation (deg) — the grid preview honours it. */
  north: number | null;
  onUpload: (file: File) => void;
  /** attach a plan from a file already on the project (no fresh upload). */
  onPickProjectFile: (src: FloorPlanSource) => void;
  onClearPlan: () => void;
  /** open the crop + rotate editor for this floor's plan (non-destructive bake).
   *  Absent → the button is hidden (back-compat for any other caller). */
  onEditPlan?: () => void;
  /** Persist the boundary. `commit` distinguishes an in-progress trace edit
   *  (append / undo / vertex-drag → store the points VERBATIM in click order,
   *  NO cleaning) from a COMMIT (boundary closed → run the clean: dedupe +
   *  CW-winding normalise + door re-map). Cleaning mid-trace REVERSED the
   *  partial polygon's winding between clicks and scrambled click order, drawing
   *  a self-intersecting "cross line" (owner bug 2026-06-08). Cleaning only at
   *  commit keeps the live trace in pure click order. */
  onChangeBoundary: (pts: Pt[], commit?: boolean) => void;
  uploading?: boolean;
  /** image files already on the project (the Files section). */
  projectImageFiles?: FloorPlanSource[];
  /** Stage-3b door markers (door = width-rectangle on the wall; covered padas
   *  DERIVED from the grid). */
  entranceMarks?: EntranceMark[];
  /** ONE-SHOT placement (item 2): armed by the "+ Add door" button, the NEXT
   *  single click on a wall snaps a door of default width to the nearest edge
   *  (the parent derives covered padas off the grid), then disarms. */
  onAddEntrance?: (click: Pt) => void;
  /** ONE-SHOT placement of an INTERNAL door: armed by "+ Add internal door", the
   *  NEXT single click INSIDE the boundary drops a free marker (off a staircase /
   *  lift lobby) whose pada is read by direction from the centroid, then disarms. */
  onAddInternalEntrance?: (click: Pt) => void;
  /** drag a door body → a perimeter door SLIDES along its wall; an internal door
   *  TRANSLATES freely. Padas re-derive live (the parent routes by placement). */
  onMoveEntrance?: (id: string, drag: Pt) => void;
  /** drag one of a door's two END grips → set the exact span visually. A
   *  perimeter grip rides the wall (re-snapped); an internal grip moves FREELY
   *  (rotate / resize). The opposite end stays anchored; padas re-derive live. */
  onSetEntranceEnd?: (id: string, which: 'start' | 'end', drag: Pt) => void;
  onRemoveEntrance?: (id: string) => void;
  onSetEntranceType?: (id: string, type: string) => void;
  onSetEntranceName?: (id: string, name: string) => void;
  /** designate this door as the principal/main entry (one per floor). */
  onSetPrincipal?: (id: string) => void;
  /** Slice-5 AUO markers (point inside the footprint → 16-zone DERIVED via
   *  zoneAtPoint — the same interior-click pattern as an internal door, read as
   *  a zone not a pada). */
  auoMarks?: AuoMark[];
  /** ONE-SHOT placement of an AUO: armed by "+ Add AUO", the NEXT single click
   *  INSIDE the boundary drops a marker whose zone is derived from the grid,
   *  then disarms. */
  onAddAuo?: (click: Pt) => void;
  /** drag an AUO marker → move its region centre; the parent re-derives zones. */
  onMoveAuo?: (id: string, drag: Pt) => void;
  onRemoveAuo?: (id: string) => void;
  /** drag a corner grip → resize the AUO shape (rect half-extents / circle
   *  radius). The parent re-derives the covered zone set. CENTRE-anchored — kept
   *  for the circle case + programmatic sizing. */
  onResizeAuo?: (id: string, size: { hw?: number; hh?: number; r?: number }) => void;
  /** drag a corner grip with the OPPOSITE corner PINNED (standard bounding-box
   *  resize — the precise-placement fix). `gripCorner` = which corner is dragged
   *  (0=TL,1=TR,2=BR,3=BL). The parent re-derives the covered zone set. When
   *  provided it is preferred over `onResizeAuo` for a rect grip drag. */
  onResizeAuoAnchored?: (id: string, gripCorner: number, drag: Pt) => void;
  /** the AUO currently selected in the review list — its marker is highlighted +
   *  pulled forward (two-way reference by number). */
  selectedAuoId?: string | null;
  /** MARKUPS (#2) — eval-canvas annotations (tick/cross/question/cloud/mirror/
   *  arrow/text), selectable/editable/deletable, numbered in the Markups list. */
  markups?: EvalMarkup[];
  onAddMarkup?: (m: EvalMarkup) => void;
  onMoveMarkup?: (id: string, drag: Pt) => void;
  onResizeMarkup?: (id: string, drag: Pt) => void;
  onRemoveMarkup?: (id: string) => void;
  selectedMarkupId?: string | null;
  onSelectMarkup?: (id: string | null) => void;
  /** click a marker → select it (highlights its review card by number). */
  onSelectAuo?: (id: string | null) => void;
  /**
   * AD-013 / L1A staff-evaluation — MARKING-ONLY mode. The `boundary` is fed in
   * from a PROPERTY's already-traced grid (the canonical, versioned asset) and is
   * LOCKED here: the plan upload/replace/crop/remove controls, the trace
   * undo/clear/close/edit controls, and the "Trace boundary" mode are all
   * suppressed; only entrance + AUO marking (+ the review/confirm list) remain.
   * The boundary cannot be edited on this surface — gridding happens ONLY on the
   * Property page. Defaults false (the full Property-page tracer is unchanged).
   */
  readOnlyBoundary?: boolean;
  /**
   * 2.1 — FULLY READ-ONLY DISPLAY (the comparative grid preview). A superset of
   * `readOnlyBoundary`: the boundary is locked AND the entrance + AUO marks are
   * display-only — every toolbar, the mode switch, the add buttons, the
   * status line, the door list, and ALL pointer interaction (canvas clicks,
   * vertex / door / AUO drag + resize, selection) are suppressed. Renders just
   * the stage: floor plan + CAD grid overlay + entrance + AUO marks. Defaults
   * false (interactive marking is unchanged).
   */
  readOnlyMarks?: boolean;
  /**
   * Phase-1 evaluation workspace (spec §3.3) — IN-FRAME ZOOM / PAN. When true,
   * ONLY the internal stage div (image + SVG overlay) is wrapped in
   * react-zoom-pan-pinch; the toolbars stay outside the transform. Marks are
   * stored in IMAGE-PIXEL space and clicks map via `getScreenCTM().inverse()`,
   * which absorbs any ancestor CSS transform — so the zoom/pan transform is
   * auto-inverted and marks stay glued at every zoom (anti-drift, spec §3.3).
   * Pan coexists with marking via a hand-tool toggle / Space-drag; otherwise
   * pointer events fall through to the marking handlers unchanged. Defaults
   * false → every existing tracer mount is byte-unchanged.
   */
  enableZoom?: boolean;
}) {
  // 2.1 — read-only marks implies a locked boundary too (display superset).
  const roBoundary = readOnlyBoundary || readOnlyMarks;
  const displayOnly = readOnlyMarks;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  // ── In-frame zoom/pan (enableZoom only — spec §3.3). Hand-tool toggle OR a
  // held Space key enables panning; otherwise pointer events fall through to the
  // marking handlers. `scalePct` is the live zoom readout, lifted from the
  // transform's onTransformed callback. Min/max clamp (0.5×–6×) + zoom-to-cursor
  // are configured on the <TransformWrapper> below. All inert when enableZoom off.
  const [panMode, setPanMode] = useState(false);
  const [spaceHeld, setSpaceHeld] = useState(false);
  // #2 — middle mouse button (wheel-click) held → pan (in addition to the hand
  // tool + Space). Set on a middle-button pointerdown over the stage, cleared on
  // release; while held, react-zoom-pan-pinch panning is enabled.
  const [midPan, setMidPan] = useState(false);
  const [scalePct, setScalePct] = useState(100);
  const zoomViewportRef = useRef<HTMLDivElement | null>(null);
  // #1 — the MEASURED available height for the map stage (the space from the stage
  // viewport's top to the map pane's bottom), so the plan auto-fits with zero clip
  // regardless of the toolbar rows' height. Null until measured (enableZoom only).
  const [fitH, setFitH] = useState<number | null>(null);
  // BUG A (2026-06-08): the boundary used to be treated as CLOSED the instant a
  // 3rd point existed (`pts.length >= 3`), which auto-generated the grid on a
  // 3-point near-triangle and signalled "done" before the user had traced the
  // real outline — they couldn't keep adding corners and got a garbled grid.
  // The boundary now closes ONLY on an explicit action: the "Close boundary"
  // button (≥3 points) OR clicking the FIRST point within a small snap radius.
  // Lazily seeded TRUE for a re-opened SAVED floor (a boundary already present
  // is, by definition, a completed trace — show its grid immediately).
  const [boundaryClosed, setBoundaryClosed] = useState<boolean>(
    () => Array.isArray(boundary) && boundary.length >= 3,
  );
  // FIRST-POINT EDIT BUG (2026-06-08): the first-vertex "click-back-on-start"
  // CLOSE gesture must apply ONLY while the boundary is being traced for the
  // FIRST time (still open, never closed). Once the boundary is closed and the
  // user presses "Edit boundary" (reopenBoundary → boundaryClosed=false), the
  // outline is OPEN again but we are EDITING a completed shape — pressing /
  // dragging the first vertex must MOVE it like any other vertex, NOT re-close
  // (owner: "not allowing to edit the first point, it auto completes"). This
  // flag is true while editing a reopened boundary; it suppresses the
  // first-vertex close gesture (both the vertex-press path and the
  // click-near-first-point canvas path) so vertex 0 drags freely. Cleared the
  // moment the boundary is (re)closed or cleared.
  const [editing, setEditing] = useState(false);
  // BUG 1: a pointerdown on a vertex / door / end-grip is followed (after
  // pointerup) by a SYNTHETIC `click` on the SVG. `onPointerUp` has already
  // reset the drag-state by then, so the click slipped past the drag guards and
  // ADDED a phantom boundary point (the "editing the boundary adds points"
  // bug). This synchronous ref is armed on any handle pointerdown and consumed
  // by the very next canvas click, swallowing it. A real empty-canvas click
  // never arms it, so deliberate add still works.
  const swallowNextClick = useRef(false);
  // BUG 1 FIX (2026-06-26): a FRESH canvas pointer-down (a new deliberate gesture,
  // NOT a drag's trailing synthetic click — those carry no pointer-down) clears
  // the swallow guard so it can never stick `true` and eat the next real trace
  // click (the reproduced "can't trace after adjusting a vertex" bug). Handle
  // pointer-downs stopPropagation, so this only fires for canvas presses.
  function onCanvasPointerDown() {
    swallowNextClick.current = false;
  }
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Click mode: trace the boundary, or mark entrances on the grid. Mutually
  // exclusive so the two click handlers never interfere.
  // Marking-only (read-only boundary) surfaces start in 'entrance' mode — there is
  // no trace step here, so default straight into marking once the locked grid
  // exists (the `activeMode` guard still falls back to 'trace' until the grid is
  // ready, but with a pre-traced property boundary the grid is ready immediately).
  const [mode, setMode] = useState<TracerMode>(
    () => (readOnlyBoundary || readOnlyMarks ? 'entrance' : 'trace'),
  );
  // which door is mid body-slide (so we ignore the click that ends it).
  const [dragEntId, setDragEntId] = useState<string | null>(null);
  // which door END grip is being dragged (item 4) — id + which end.
  const [dragEnd, setDragEnd] = useState<{ id: string; which: 'start' | 'end' } | null>(
    null,
  );
  // ONE-SHOT add (item 2): which add is armed — null, a PERIMETER door ("+ Add
  // door" → next WALL click), an INTERNAL door ("+ Add internal door" → next
  // INSIDE-boundary click), or an AUO ("+ Add AUO" → next INSIDE-boundary click,
  // Slice 5). A plain canvas click never auto-adds.
  // 'door' = the UNIFIED door placement (mockup v3.5 / decision #18 — a door is a
  // door): the next click places a door anywhere, perimeter near a wall OR internal
  // inside the plot; both get the 32-pada reading. ('perimeter'/'internal' retained
  // for back-compat callers.)
  const [armedKind, setArmedKind] = useState<null | 'door' | 'perimeter' | 'internal' | 'auo'>(null);
  // MARKUP placement (#2): which markup tool is armed (next click places it), and
  // which markup is mid body-drag / mid resize-grip-drag.
  const [armedMarkup, setArmedMarkup] = useState<MarkupType | null>(null);
  const [dragMarkupId, setDragMarkupId] = useState<string | null>(null);
  const [dragMarkupResize, setDragMarkupResize] = useState<string | null>(null);
  // which AUO marker is mid body-drag (so we ignore the click that ends it).
  const [dragAuoId, setDragAuoId] = useState<string | null>(null);
  // which AUO is mid RESIZE (corner grip drag) — id + which corner is dragged
  // (0=TL,1=TR,2=BR,3=BL). The OPPOSITE corner stays pinned (standard bounding-box
  // resize) so the staff can size + place precisely against a known edge.
  const [resizeAuoId, setResizeAuoId] = useState<string | null>(null);
  const [resizeAuoCorner, setResizeAuoCorner] = useState<number>(1);

  // Only IMAGE files can be traced (a PDF can't be a raster layer).
  const fileChoices = useMemo(
    () => imageFloorPlanSources(projectImageFiles),
    [projectImageFiles],
  );

  const pts: Pt[] = useMemo(
    () => (Array.isArray(boundary) ? boundary : []),
    [boundary],
  );
  // The drawing surface size = the image's natural pixels (so the SVG viewBox
  // is in image-pixel space), or the fallback box when no image yet.
  const W = floorplan?.width || FALLBACK_W;
  const H = floorplan?.height || FALLBACK_H;

  // Plain boolean (NOT the `pts is Pt[]` type guard) so the negative branch of
  // the status-line ternary doesn't narrow `pts` to `never`. CLOSED now requires
  // BOTH the explicit close flag AND ≥3 points (a guard against a stale flag if
  // the trace is later cut below 3 by Undo / Clear). No more auto-close at 3.
  const closed = boundaryClosed && pts.length >= 3;
  // Safety net: if the boundary is edited below 3 points, drop the closed flag.
  useEffect(() => {
    if (boundaryClosed && pts.length < 3) setBoundaryClosed(false);
  }, [boundaryClosed, pts.length]);
  // SYMMETRIC RE-CLOSE (2026-07-01 bug fix): the `boundaryClosed` useState is
  // seeded from the INITIAL `boundary` prop, which on the L1A locked-grid marking
  // surface arrives ASYNC (seeded from the linked property grid AFTER mount). If
  // it seeds false and the boundary lands late, nothing ever re-raises the flag →
  // `closed` stays false → `grid` is null → the map pins (entrances/AUOs, gated
  // on `grid`) never draw on reload, even though the right-side list (ungated)
  // shows them. On a READ-ONLY / locked marking surface a present ≥3-pt boundary
  // is by definition a completed trace, so close it. Gated on
  // `readOnlyBoundary || readOnlyMarks` so an in-progress trace on the EDITABLE
  // surface is never auto-closed.
  useEffect(() => {
    if (!boundaryClosed && !editing && (readOnlyBoundary || readOnlyMarks) && pts.length >= 3) {
      setBoundaryClosed(true);
    }
  }, [boundaryClosed, editing, readOnlyBoundary, readOnlyMarks, pts.length]);
  // The grid + overlay are OUTPUTS of a completed trace — compute only once the
  // boundary is explicitly closed (was: any time ≥3 points existed).
  const grid: GridResult | null = useMemo(() => {
    if (!closed || !isValidBoundary(pts)) return null;
    try {
      return computeGrid({ boundary: pts, rotation: north ?? 0 });
    } catch {
      return null;
    }
  }, [closed, pts, north]);

  // CAD OVERLAY (locked spec — memory `reference_grid_cad_overlay_spec`).
  // Faithful port of the v7 preview: cleaned boundary → edge-parallel zone
  // colour strip JUST OUTSIDE, 32 dividers, two radial label rings (pada code +
  // zone name) anchored to the FARTHEST boundary hit, bracket ticks. Computed on
  // the CLEANED boundary + its own centroid/L (exactly like the preview), so the
  // S3 near-duplicate artefact is gone. `grid` (engine-parity) stays unchanged
  // and still drives door-pada derivation — this is a rendering layer only.
  const overlay = useMemo(() => {
    if (!closed || !isValidBoundary(pts)) return null;
    const B = cleanBoundary(pts);
    if (B.length < 3) return null;
    const rot = north ?? 0;
    const C = shoelaceCentroid(B);
    const m = overlayMetrics(B);
    // 32-ray ring on the CLEANED boundary (nearest hit — interior dividers).
    const L: Pt[] = [];
    for (let i = 1; i <= 32; i++) {
      const deg = ((i - 1) * 11.25 + rot + 360) % 360;
      const { dx, dy } = rayDir(deg);
      let best: { x: number; y: number; t: number } | null = null;
      for (let k = 0; k < B.length; k++) {
        const h = (() => {
          const p1 = B[k];
          const p2 = B[(k + 1) % B.length];
          const sx = p2.x - p1.x;
          const sy = p2.y - p1.y;
          const den = dx * -sy - dy * -sx;
          if (Math.abs(den) < 1e-9) return null;
          const t = ((p1.x - C.x) * -sy - (p1.y - C.y) * -sx) / den;
          const s = (dx * (p1.y - C.y) - dy * (p1.x - C.x)) / den;
          if (t >= 0 && s >= -1e-6 && s <= 1 + 1e-6)
            return { x: C.x + t * dx, y: C.y + t * dy, t };
          return null;
        })();
        if (h && (!best || h.t < best.t)) best = h;
      }
      L.push(best ? { x: best.x, y: best.y } : { x: C.x + dx * 100, y: C.y + dy * 100 });
    }
    // edge-parallel ZONE colour strip (no pada strip), split at the 16 zone dividers.
    const strip = zoneStrip(B, C, 0, m.STRIP, zoneDividerDegs(rot), (ang) =>
      zoneColour(zoneOfAngle(ang, rot)),
    );
    // pada (inner) + zone (outer) radial labels, anchored to FARTHEST hit.
    const padaLabels = [] as {
      key: string; x: number; y: number; rotate: number; text: string; col: string;
    }[];
    for (let k = 1; k <= 32; k++) {
      const mid = (k - 0.5) * 11.25 + rot;
      const lbl = ENTRANCE_LABELS[k] as string;
      const place = radialLabel(C, mid, m.PAD_LBL, B);
      padaLabels.push({ key: `pl${k}`, ...place, text: lbl, col: padaColour(lbl) });
    }
    const zoneLabels = [] as {
      key: string; x: number; y: number; rotate: number; text: string; col: string;
    }[];
    for (let z = 0; z < 16; z++) {
      const cen = z * 22.5 + rot;
      const place = radialLabel(C, cen, m.ZON_LBL, B);
      zoneLabels.push({
        key: `zl${z}`, ...place, text: ZONE_LABELS[z], col: ZONE_OVERLAY_COLOURS[z],
      });
    }
    // bracket ticks (zone = longer/darker, pada = shorter/lighter).
    const ticks = [] as { key: string; x1: number; y1: number; x2: number; y2: number; isZone: boolean }[];
    for (let i = 1; i <= 32; i++) {
      const isZone = i % 2 === 0;
      const t = dividerTick(C, i, rot, m.BREAK, isZone ? m.ZON_END : m.PAD_END, B);
      ticks.push({ key: `tk${i}`, ...t, isZone });
    }
    return { B, C, L, strip, padaLabels, zoneLabels, ticks, metrics: m };
    // `closed` MUST be a dep: the overlay short-circuits on `!closed`, and the
    // close action flips `closed` false→true WITHOUT changing `pts` (the commit
    // re-feeds the same points). Without `closed` here the memo never recomputed
    // after an in-session close, so a freshly-committed boundary showed GRID ✓
    // but rendered NO overlay (it stayed the stale `null` from when it was open).
    // A re-opened SAVED floor worked only because `closed` is seeded true at
    // mount, so the first computation already saw closed===true.
  }, [closed, pts, north]);

  /** Map a pointer event to image-pixel coordinates via the SVG CTM. */
  function toImagePt(e: React.PointerEvent | React.MouseEvent): Pt | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = (e as React.MouseEvent).clientX;
    pt.y = (e as React.MouseEvent).clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  // ── Zoom/pan keyboard layer (enableZoom only — spec §3.3). Space = momentary
  // pan (held); ← ↑ → ↓ nudge the SELECTED AUO by 1 image-pixel (wires to the
  // existing onMoveAuo patch handler, so the move re-derives the zone exactly
  // like a drag). Listeners are scoped to the stage viewport so they never steal
  // keys from the rest of the page. No-op when enableZoom is false.
  useEffect(() => {
    if (!enableZoom) return;
    const el = zoomViewportRef.current;
    if (!el) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === ' ' || e.code === 'Space') {
        // only hijack Space when the stage (not a text field) is focused.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        setSpaceHeld(true);
        return;
      }
      // arrow-key nudge of the selected AUO by 1 image-pixel.
      if (!selectedAuoId || !onMoveAuo) return;
      const delta: Record<string, [number, number]> = {
        ArrowLeft: [-1, 0],
        ArrowRight: [1, 0],
        ArrowUp: [0, -1],
        ArrowDown: [0, 1],
      };
      const d = delta[e.key];
      if (!d) return;
      const mark = auoMarks.find((m) => m.id === selectedAuoId);
      if (!mark) return;
      e.preventDefault();
      const reg = regionForMark(mark);
      onMoveAuo(selectedAuoId, { x: reg.cx + d[0], y: reg.cy + d[1] });
    }
    function onKeyUp(e: KeyboardEvent) {
      if (e.key === ' ' || e.code === 'Space') setSpaceHeld(false);
    }
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', onKeyUp);
    return () => {
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('keyup', onKeyUp);
    };
  }, [enableZoom, selectedAuoId, onMoveAuo, auoMarks]);

  // Entrance- and AUO-marking are only possible once the grid exists (the pada /
  // zone are read off the grid). Until then the mode is forced to 'trace'.
  const canMarkEntrances = grid != null && !!onAddEntrance;
  const canMarkAuo = grid != null && !!onAddAuo;
  // A selected mode is honoured only when its capability exists; else fall to
  // 'trace'. (entrance needs canMarkEntrances; auo needs canMarkAuo.)
  const activeMode: TracerMode =
    mode === 'markup' && onAddMarkup ? 'markup'
    : mode === 'entrance' ? (canMarkEntrances ? 'entrance' : 'trace')
    : mode === 'auo' ? (canMarkAuo ? 'auo' : 'trace')
    : 'trace';

  // #1 — measure the available height for the map stage at RUNTIME (the distance
  // from the stage viewport's top to the map pane's bottom), via a ResizeObserver
  // on the pane + a re-measure whenever the toolbar rows change height (mode /
  // armed-tool switches). The stage is then capped to this height with an
  // aspect-derived width — the whole plan fits with no clip, no distortion.
  useLayoutEffect(() => {
    if (!enableZoom) return;
    const vp = zoomViewportRef.current;
    if (!vp) return;
    const pane = (vp.closest('.evalws-mappane') as HTMLElement | null) || vp.parentElement;
    if (!pane) return;
    const measure = () => {
      const paneRect = pane.getBoundingClientRect();
      const vpRect = vp.getBoundingClientRect();
      const h = Math.max(140, Math.floor(paneRect.bottom - vpRect.top - 6));
      setFitH((prev) => (prev != null && Math.abs(prev - h) < 2 ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(pane);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    // re-measure when the plan or the toolbar rows change (these move the stage top).
  }, [enableZoom, floorplan?.downloadURL, activeMode, armedKind, armedMarkup]);

  /** A click on the canvas — routed by the ACTIVE mode. In trace mode it adds a
   *  boundary vertex (deliberate click-to-add — unchanged). In entrance mode it
   *  adds a door ONLY when armed by "+ Add door" (item 2 — a plain click pans /
   *  selects, never auto-adds); the armed click places ONE door then disarms. */
  function onCanvasClick(e: React.MouseEvent) {
    // BUG 1: swallow the synthetic click that fires right after a vertex / door /
    // end-grip interaction (pointerup already cleared the drag-state, so the
    // older `dragIdx != null` guards no longer catch it). Without this, grabbing
    // OR even just tapping a vertex appended a phantom point.
    if (swallowNextClick.current) {
      swallowNextClick.current = false;
      return;
    }
    // ignore the click that ends a vertex / door slide / end-grip / AUO / markup drag
    if (dragIdx != null || dragEntId != null || dragEnd != null || dragAuoId != null || dragMarkupId != null || dragMarkupResize != null) return;
    const p = toImagePt(e);
    if (!p) return;
    if (activeMode === 'markup') {
      // MARKUP placement (#2): with a tool armed, click drops that annotation at
      // the point (point glyphs centred; rect/arrow get a default span). Stays
      // armed for rapid multi-placement; a plain click clears the selection.
      if (armedMarkup) {
        onAddMarkup?.(makeMarkup(armedMarkup, p, Math.max(W, H)));
      } else {
        onSelectMarkup?.(null);
      }
      return;
    }
    if (activeMode === 'auo') {
      // AUO marking (Slice 5): a one-shot ARMED click drops a fixture INSIDE the
      // plot — its 16-zone is derived from the grid (same interior-click pattern
      // as an internal door). A click outside the plot stays armed (the staff
      // clicks again inside); a plain click selects/pans, never auto-adds.
      if (armedKind === 'auo' && isValidBoundary(pts) && pointInPolygon(p, pts)) {
        onAddAuo?.(p);
        setArmedKind(null);
      }
      return;
    }
    if (activeMode === 'entrance') {
      // ITEM 2: only a one-shot ARMED click drops a door; otherwise do nothing
      // (clicks to pan/adjust no longer spawn stray doors).
      if (armedKind === 'door') {
        // UNIFIED door (decision #18 — a door is a door, placed ANYWHERE): if the
        // click is near a wall, snap a PERIMETER door (pada by wall span); else, if
        // inside the plot, drop an INTERNAL door (pada by direction). Both get the
        // 32-pada reading. A click far outside the plot stays armed.
        if (!isValidBoundary(pts)) return;
        const hit = nearestEdge(p, pts);
        const nearWall = hit ? hit.distance <= Math.max(W, H) / 14 : false;
        if (nearWall) {
          onAddEntrance?.(p);
          setArmedKind(null);
        } else if (pointInPolygon(p, pts)) {
          onAddInternalEntrance?.(p);
          setArmedKind(null);
        }
      } else if (armedKind === 'perimeter') {
        onAddEntrance?.(p);
        setArmedKind(null);
      } else if (armedKind === 'internal') {
        // INTERNAL door: placed FREELY inside the boundary (not snapped). Guard
        // against a click outside the plot so an internal marker never lands off
        // the footprint (its direction-pada would be meaningless). A click
        // outside stays armed — the staff just clicks again inside.
        if (isValidBoundary(pts) && pointInPolygon(p, pts)) {
          onAddInternalEntrance?.(p);
          setArmedKind(null);
        }
      }
      return;
    }
    // TRACE mode. On a MARKING-ONLY surface the boundary is LOCKED (sourced from
    // the property grid) — a trace click must never add/edit a vertex here.
    if (roBoundary) return;
    // Once the boundary is closed, a canvas click does nothing — editing is by
    // dragging vertices (or "Edit boundary" to add points again).
    if (closed) return;
    // CLOSE GESTURE (BUG A): with ≥3 points, clicking the FIRST point within a
    // SMALL snap radius closes the boundary (the standard polygon-trace gesture)
    // instead of adding a coincident point. The radius is deliberately small so
    // a normal click anywhere else keeps adding corners freely. ONLY while
    // tracing for the first time (`!editing`) — when EDITING a reopened
    // boundary, a click near the first point must NOT re-close (the user is
    // adjusting the shape; vertex 0 is dragged via its handle).
    if (!editing && pts.length >= 3 && isNearFirstPoint(p)) {
      commitBoundary();
      return;
    }
    // append a vertex in CLICK ORDER — VERBATIM, no clean (commit=false). The
    // clean (dedupe + CW normalise) runs ONLY on close, so the partial polygon
    // is never reordered/reversed between clicks (the cross-line bug).
    onChangeBoundary([...pts, { x: Math.round(p.x), y: Math.round(p.y) }], false);
  }

  /** SMALL snap radius (image-pixel units) for the click-to-close gesture —
   *  just larger than the visible first-vertex handle so it's comfortable to
   *  hit but never fires on an ordinary corner click elsewhere. */
  function isNearFirstPoint(p: Pt): boolean {
    const first = pts[0];
    if (!first) return false;
    const r = Math.max(W, H) / 45;
    return Math.hypot(p.x - first.x, p.y - first.y) <= r;
  }

  function undo() {
    if (pts.length === 0) return;
    // still tracing — keep the remaining points VERBATIM (no clean/reorder).
    onChangeBoundary(pts.slice(0, -1), false);
  }
  function clearAll() {
    setBoundaryClosed(false);
    setEditing(false); // a fresh trace, not an edit of a completed shape
    onChangeBoundary([], false);
  }
  /** COMMIT the boundary (close): flip the closed flag AND run the parent clean
   *  (dedupe + CW normalise + door re-map) ONCE, now that the outline is final.
   *  Re-feeds the SAME points with commit=true. Needs ≥3 points; idempotent. */
  function commitBoundary() {
    if (pts.length < 3) return;
    setBoundaryClosed(true);
    setEditing(false); // closed again → no longer editing an open reopened shape
    onChangeBoundary(pts, true);
  }
  /** Explicit close (button) — needs ≥3 points; idempotent. */
  function closeBoundary() {
    commitBoundary();
  }
  /** Re-open a closed boundary so corners can be added/removed again. The grid
   *  hides until it's closed again (it's recomputed from the edited outline). */
  function reopenBoundary() {
    setBoundaryClosed(false);
    // EDITING a completed shape: the outline is open again, but pressing the
    // first vertex must DRAG it, not re-close (first-point edit bug). The
    // editing flag suppresses the first-vertex close gesture until the user
    // explicitly closes again (button) or clears.
    setEditing(true);
    setMode('trace');
    setArmedKind(null);
  }

  function onVertexDown(i: number, e: React.PointerEvent) {
    if (activeMode !== 'trace') return; // vertices are inert in entrance mode
    e.stopPropagation();
    // CLOSE GESTURE (BUG A): while tracing for the FIRST time, pressing the
    // FIRST vertex with ≥3 points closes the boundary (the classic "click back
    // on the start point" gesture) rather than starting a drag. This fires ONLY
    // during the initial open trace (`!closed && !editing`). Once the boundary
    // is closed — OR reopened for editing (`editing`) — the first vertex DRAGS
    // like any other (first-point edit bug fix 2026-06-08: previously this
    // condition was just `!closed`, so a reopened boundary auto-closed the
    // moment the user pressed vertex 0 to move it).
    if (!closed && !editing && i === 0 && pts.length >= 3) {
      swallowNextClick.current = true; // eat the trailing synthetic click
      commitBoundary(); // close + clean (dedupe + CW normalise) in one go
      return;
    }
    // start a vertex interaction. The swallow guard is armed at pointer-UP (for
    // the trailing synthetic click) and self-clears on the next fresh canvas
    // pointer-down — so it never sticks (see onPointerUp / onCanvasPointerDown).
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragIdx(i);
  }
  function onEntranceDown(id: string, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragEntId(id);
  }
  function onEndGripDown(id: string, which: 'start' | 'end', e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragEnd({ id, which });
  }
  function onAuoDown(id: string, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragAuoId(id);
    // pressing a marker also SELECTS it (highlights its numbered review card).
    onSelectAuo?.(id);
  }
  /** Press a corner grip → start resizing the AUO shape with the OPPOSITE corner
   *  PINNED. `corner` = which corner is grabbed (0=TL,1=TR,2=BR,3=BL). */
  function onAuoResizeDown(id: string, corner: number, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setResizeAuoId(id);
    setResizeAuoCorner(corner);
    onSelectAuo?.(id);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragIdx != null) {
      const p = toImagePt(e);
      if (!p) return;
      const next = pts.slice();
      next[dragIdx] = { x: Math.round(p.x), y: Math.round(p.y) };
      // moving ONE vertex must not reorder/reverse the rest mid-drag — store
      // verbatim (commit=false) so the dragged vertex keeps its identity; the
      // already-closed polygon is re-committed/cleaned on pointer-up.
      onChangeBoundary(next, false);
      return;
    }
    if (dragEnd != null) {
      // drag one END grip along the wall — the parent re-projects + rebuilds the
      // span from (anchor, moved-end), re-deriving covered padas live (item 4).
      const p = toImagePt(e);
      if (!p) return;
      onSetEntranceEnd?.(dragEnd.id, dragEnd.which, p);
      return;
    }
    if (dragEntId != null) {
      const p = toImagePt(e);
      if (!p) return;
      onMoveEntrance?.(dragEntId, p);
      return;
    }
    if (resizeAuoId != null) {
      // drag a corner grip → GRIP-ANCHORED resize: the OPPOSITE corner stays
      // PINNED, the shape resizes toward the grip (standard bounding-box resize),
      // so the staff can size + place precisely. The anchored op handles both the
      // rect (local-frame box rebuilt around the pinned corner) and the circle
      // (left edge pinned). Falls back to the legacy centre-resize only if the
      // parent did not wire the anchored handler.
      const p = toImagePt(e);
      if (!p) return;
      if (onResizeAuoAnchored) {
        onResizeAuoAnchored(resizeAuoId, resizeAuoCorner, p);
        return;
      }
      const m = auoMarks.find((x) => x.id === resizeAuoId);
      if (!m) return;
      const reg = regionForMark(m);
      const dx = p.x - reg.cx;
      const dy = p.y - reg.cy;
      if (reg.kind === 'circle') {
        onResizeAuo?.(resizeAuoId, { r: Math.hypot(dx, dy) });
      } else {
        const r = (-reg.rotation * Math.PI) / 180;
        const lx = dx * Math.cos(r) - dy * Math.sin(r);
        const ly = dx * Math.sin(r) + dy * Math.cos(r);
        onResizeAuo?.(resizeAuoId, { hw: Math.abs(lx), hh: Math.abs(ly) });
      }
      return;
    }
    if (dragAuoId != null) {
      // drag an AUO marker → move its region; the parent re-derives zones live.
      const p = toImagePt(e);
      if (!p) return;
      onMoveAuo?.(dragAuoId, p);
      return;
    }
    if (dragMarkupResize != null) {
      const p = toImagePt(e);
      if (!p) return;
      onResizeMarkup?.(dragMarkupResize, p);
      return;
    }
    if (dragMarkupId != null) {
      const p = toImagePt(e);
      if (!p) return;
      onMoveMarkup?.(dragMarkupId, p);
    }
  }
  function onMarkupDown(id: string, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragMarkupId(id);
    onSelectMarkup?.(id);
  }
  function onMarkupResizeDown(id: string, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragMarkupResize(id);
    onSelectMarkup?.(id);
  }
  function onPointerUp() {
    const wasInteracting =
      dragIdx != null || dragEntId != null || dragEnd != null ||
      dragAuoId != null || resizeAuoId != null ||
      dragMarkupId != null || dragMarkupResize != null;
    // a vertex drag just ended on a CLOSED boundary → re-commit so the final
    // shape is cleaned (dedupe + CW normalise) and doors re-map. No-op while
    // tracing (open) — the trace stays verbatim until explicit close.
    if (dragIdx != null && closed && pts.length >= 3) {
      onChangeBoundary(pts, true);
    }
    // Arm the swallow after ANY handle interaction — a tap OR a drag both fire a
    // trailing synthetic `click` (Chromium fires it even after a drag within the
    // element), which must be eaten so it never adds a phantom boundary point.
    // The guard CANNOT stick (the old bug) because the next FRESH canvas
    // pointer-down clears it (onCanvasPointerDown) — a new deliberate click is
    // never a stale trailing click. (BUG 1 FIX 2026-06-26.)
    if (wasInteracting) {
      swallowNextClick.current = true;
    }
    setDragIdx(null);
    setDragEntId(null);
    setDragEnd(null);
    setDragAuoId(null);
    setResizeAuoId(null);
    setDragMarkupId(null);
    setDragMarkupResize(null);
  }

  const polyPoints = pts.map((p) => `${p.x},${p.y}`).join(' ');

  // SVG viewBox — image-pixel space. When the CAD grid overlay is visible we
  // EXPAND the box by a diag-derived margin (mirrors the v7 preview `VB`) so the
  // outside colour strip + radial labels are not clipped. Coordinates STAY in
  // image-pixel units (only the origin shifts), so `toImagePt`'s CTM math and
  // every click→pixel mapping are unchanged. Aspect ratio of the stage tracks
  // the same box so the floor plan never distorts.
  const view = useMemo(() => {
    if (overlay && showGrid) {
      let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
      for (const p of overlay.B) {
        if (p.x < minx) minx = p.x;
        if (p.y < miny) miny = p.y;
        if (p.x > maxx) maxx = p.x;
        if (p.y > maxy) maxy = p.y;
      }
      const M = 0.215 * overlay.metrics.diag;
      const vx = minx - M;
      const vy = miny - M * 0.8;
      const vw = maxx - minx + 2 * M;
      const vh = maxy - miny + 1.85 * M;
      return { x: vx, y: vy, w: vw, h: vh, box: `${vx} ${vy} ${vw} ${vh}` };
    }
    return { x: 0, y: 0, w: W, h: H, box: `0 0 ${W} ${H}` };
  }, [overlay, showGrid, W, H]);

  /**
   * Spec §3.3 — wrap ONLY the internal stage (image + SVG overlay) in
   * react-zoom-pan-pinch when `enableZoom` is set. The CSS transform it applies
   * is absorbed by `svg.getScreenCTM().inverse()` in `toImagePt`, so clicks keep
   * mapping to the correct image-pixel at every zoom (marks never drift).
   * Panning is DISABLED unless the hand tool is on OR Space is held — so a normal
   * pointer drag still reaches the marking handlers. Returns the stage unchanged
   * when `enableZoom` is false (every existing mount is byte-identical).
   */
  function renderStage(stageEl: React.ReactNode) {
    if (!enableZoom) return stageEl;
    return (
      <div
        className="evalws-zoom-viewport"
        ref={zoomViewportRef}
        tabIndex={0}
        aria-label="Floor plan stage — zoomable. Scroll or use the zoom controls to zoom, hold Space or the middle mouse button to pan, arrow keys nudge the selected AUO by one pixel."
        onPointerDownCapture={(e) => {
          if (e.button === 1) {
            // middle button → pan; capture so the SVG marking handlers don't fire,
            // and preventDefault to stop the browser's autoscroll cursor.
            e.preventDefault();
            setMidPan(true);
          }
        }}
        onPointerUp={(e) => { if (e.button === 1) setMidPan(false); }}
        onPointerLeave={() => setMidPan(false)}
      >
        <TransformWrapper
          minScale={0.5}
          maxScale={6}
          initialScale={1}
          centerOnInit
          limitToBounds={false}
          doubleClick={{ disabled: true }}
          wheel={{ step: 0.12 }}
          pinch={{ step: 5 }}
          panning={{ disabled: !(panMode || spaceHeld || midPan), velocityDisabled: true }}
          onTransformed={(_ref, state) => setScalePct(Math.round(state.scale * 100))}
        >
          <TransformComponent
            wrapperStyle={{ width: '100%', cursor: panMode || spaceHeld ? 'grab' : undefined }}
            contentStyle={{ width: '100%' }}
          >
            {stageEl}
          </TransformComponent>
          <ZoomPanControls
            scalePct={scalePct}
            panMode={panMode}
            onTogglePan={() => setPanMode((v) => !v)}
          />
        </TransformWrapper>
      </div>
    );
  }

  return (
    <div>
      {/* toolbar — 2.1: fully suppressed in read-only DISPLAY mode (no upload,
          no trace, no grid toggle); the comparative preview shows only the stage. */}
      <div
        style={{
          display: displayOnly ? 'none' : 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        {/* MARKING-ONLY (roBoundary): the grid is sourced + locked from the
            Property page, so the plan-upload + boundary-trace affordances are all
            suppressed; only the locked-grid hint + the Show-grid toggle remain. */}
        {roBoundary && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink-soft, var(--ink-mute))',
            }}
            title="The boundary + grid were traced on the Property page and are locked here. Mark entrances and AUO on this grid."
          >
            🔒 Grid locked — traced on the Property page
          </span>
        )}
        {!roBoundary && (
        <input
          ref={fileRef}
          type="file"
          accept={FLOORPLAN_UPLOAD_ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onUpload(f);
            e.target.value = '';
          }}
        />
        )}
        {!roBoundary && (<>
        <Btn3
          intent="ink"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading
            ? 'Uploading…'
            : floorplan
              ? 'Replace floor plan'
              : 'Upload floor plan'}
        </Btn3>
        {/* Start a floor from a file ALREADY on the project (no re-upload). */}
        <div style={{ position: 'relative' }}>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setPickerOpen((o) => !o)}
            disabled={uploading}
            title={
              fileChoices.length
                ? 'Attach a floor plan already uploaded to this project'
                : 'No image files on this project yet'
            }
          >
            Choose from project files{fileChoices.length ? ` (${fileChoices.length})` : ''} ▾
          </Btn3>
          {pickerOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: 4,
                zIndex: 20,
                minWidth: 240,
                maxWidth: 320,
                maxHeight: 260,
                overflowY: 'auto',
                background: '#fff',
                border: '1px solid var(--rule-strong)',
                borderRadius: 'var(--r-sm)',
                boxShadow: 'var(--e-2, 0 6px 20px rgba(0,0,0,0.12))',
                padding: 4,
              }}
            >
              {fileChoices.length === 0 ? (
                <p
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-mute)',
                    padding: '8px 10px',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  No image files on this project yet — upload a floor plan in
                  the Files section below, or use “Upload floor plan”.
                </p>
              ) : (
                fileChoices.map((src) => (
                  <button
                    key={src.fileId}
                    type="button"
                    onClick={() => {
                      setPickerOpen(false);
                      onPickProjectFile(src);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      width: '100%',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: 'var(--r-sm)',
                      padding: '7px 10px',
                      fontSize: 13,
                      fontFamily: 'inherit',
                      color: 'var(--ink)',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'var(--inset, #f6f7f9)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'transparent')
                    }
                  >
                    <span aria-hidden>🖼</span>
                    <span style={{ wordBreak: 'break-all' }}>{src.fileName}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {floorplan && onEditPlan && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={onEditPlan}
            disabled={uploading}
            title="Rotate to align with north + crop out clutter (keeps the original)"
          >
            Crop &amp; rotate
          </Btn3>
        )}
        {/* "Remove plan" removed (#8) — "Replace floor plan" is the only plan op;
            removing a plan has no meaningful after-state. onClearPlan is retained
            in the prop API but no longer surfaced here. */}
        <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--rule)' }} />
        <Btn3 intent="ghost" size="sm" onClick={undo} disabled={pts.length === 0}>
          Undo point
        </Btn3>
        <Btn3 intent="ghost" size="sm" onClick={clearAll} disabled={pts.length === 0}>
          Clear trace
        </Btn3>
        {/* EXPLICIT close (BUG A): the boundary closes + the grid generates ONLY
            when the user says so — never auto at 3 points. Shown while tracing
            (open) once ≥3 points exist; replaced by "Edit boundary" once closed
            so corners can be added/removed again. */}
        {!closed && activeMode === 'trace' && (
          <Btn3
            intent="grn"
            size="sm"
            onClick={closeBoundary}
            disabled={pts.length < 3}
            title={
              pts.length < 3
                ? 'Add at least 3 points first'
                : 'Close the outline and generate the grid (or click the first point)'
            }
          >
            Close boundary & generate grid
          </Btn3>
        )}
        {closed && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={reopenBoundary}
            title="Re-open the outline to add or remove corners; the grid regenerates when you close it again"
          >
            Edit boundary
          </Btn3>
        )}
        </>)}
        {closed && (
          <label
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              display: 'inline-flex',
              gap: 5,
              alignItems: 'center',
            }}
          >
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
            />
            Show grid
          </label>
        )}
      </div>

      {/* MODE SWITCH — trace the boundary vs mark entrances vs mark AUO on the
          grid. The three click modes are mutually exclusive so they never
          interfere. Entrance + AUO modes unlock only once the grid exists (the
          pada / zone are read off it). */}
      {!displayOnly && (onAddEntrance || onAddAuo || onAddMarkup) && (
        <div className="evalws-mode-group" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', marginBottom: 8, maxWidth: '100%' }}>
        <div
          role="tablist"
          aria-label="Marking mode"
          style={{
            display: 'inline-flex',
            border: '1px solid var(--rule-strong)',
            borderRadius: 'var(--r-sm)',
            overflow: 'hidden',
            flex: '0 0 auto',
          }}
        >
          {([
            // MARKING-ONLY: no 'Trace boundary' tab — the grid is locked.
            ...(roBoundary ? [] : (['trace'] as const)),
            ...(onAddEntrance ? (['entrance'] as const) : []),
            ...(onAddAuo ? (['auo'] as const) : []),
            ...(onAddMarkup ? (['markup'] as const) : []),
          ] as TracerMode[]).map((m, mi, arr) => {
            const on = activeMode === m;
            const disabled =
              (m === 'entrance' && !canMarkEntrances) ||
              (m === 'auo' && !canMarkAuo);
            return (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={on}
                disabled={disabled}
                onClick={() => {
                  setMode(m);
                  // switching mode disarms any pending one-shot add (item 2).
                  setArmedKind(null);
                  setArmedMarkup(null);
                }}
                title={
                  disabled
                    ? 'Trace the boundary first — the grid must exist before you can mark on it'
                    : m === 'trace'
                      ? 'Click to add boundary points; drag a point to adjust'
                      : m === 'entrance'
                        ? 'Press “+ Add door”, then click the plan to place a door anywhere — near a wall (perimeter) or inside (internal); both read their 32-pada'
                        : m === 'auo'
                          ? 'Press “+ Add AUO”, then click INSIDE the plot to place a fixture — its zone is read off the grid'
                          : 'Annotate the plan — pick a markup tool, then click to place it (selectable / movable / deletable)'
                }
                style={{
                  border: 'none',
                  borderRight:
                    mi < arr.length - 1 ? '1px solid var(--rule-strong)' : 'none',
                  padding: '6px 14px',
                  fontSize: 12.5,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  background: on ? 'var(--ink)' : 'transparent',
                  color: on ? '#fff' : disabled ? 'var(--ink-mute)' : 'var(--ink)',
                  opacity: disabled ? 0.55 : 1,
                }}
              >
                {m === 'trace'
                  ? '✐ Boundary'
                  : m === 'entrance'
                    ? '⊙ Mark door'
                    : m === 'auo'
                      ? '◈ Mark AUO'
                      : '✎ Markups'}
              </button>
            );
          })}
        </div>

        {/* contextual controls open INLINE after the modes, past a vertical
            divider — never on a new row (Track A #2). */}
        {(activeMode === 'entrance' || activeMode === 'auo' || activeMode === 'markup') && (
          <span aria-hidden="true" style={{ width: 1, alignSelf: 'stretch', minHeight: 28, background: 'var(--rule-strong)', flex: '0 0 auto' }} />
        )}

        {/* MARKUPS tray (#2) — opens inline when Markups mode is active. Pick a
            tool, then click the plan to place that annotation. */}
        {!displayOnly && onAddMarkup && activeMode === 'markup' && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', flex: '1 1 auto', minWidth: 0 }}>
            {MARKUP_TYPES.map((t) => {
              const armed = armedMarkup === t;
              const glyph: Record<MarkupType, string> = { tick: '✓', cross: '✗', question: '?', cloud: '☁', mirror: '▥', arrow: '→', text: 'T' };
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setArmedMarkup((k) => (k === t ? null : t))}
                  title={`${MARKUP_LABEL[t]} — click the plan to place`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    border: `1px solid ${armed ? 'var(--ai)' : 'var(--rule-strong)'}`,
                    borderRadius: 'var(--r-xs)', padding: '5px 9px', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: 11.5, fontWeight: 600,
                    background: armed ? 'var(--ai)' : 'var(--surface)',
                    color: armed ? 'var(--paper)' : 'var(--ink)',
                  }}
                >
                  <span style={{ fontWeight: 700 }}>{glyph[t]}</span> {MARKUP_LABEL[t]}
                </button>
              );
            })}
            {armedMarkup && (
              <span style={{ fontSize: 11, color: 'var(--ai-ink, #5b21b6)', marginLeft: 4 }}>
                click the plan to place…
              </span>
            )}
          </div>
        )}

      {/* ITEM 2 — explicit one-shot ADD buttons. In entrance mode, pressing a
          button ARMS placement; the next single click drops ONE door and
          disarms. "+ Add door" = a PERIMETER door (next WALL click snaps to the
          nearest edge). "+ Add internal door" = an INTERNAL door (next click
          INSIDE the plot drops a free marker, for an entry off a staircase /
          lift lobby — its pada is read by direction from the centre). Without
          arming, canvas clicks select/pan and never spawn a stray door. */}
      {!displayOnly && onAddEntrance && activeMode === 'entrance' && (
        <div
          style={{
            display: 'inline-flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'nowrap',
            flex: '0 0 auto',
          }}
        >
          {/* UNIFIED door (mockup v3.5 / decision #18, #24): ONE "+ Add door".
              The next click places a door ANYWHERE — perimeter if near a wall,
              internal if inside the plot — both read their 32-pada. No
              peripheral/internal split in the UI. */}
          <Btn3
            intent={armedKind === 'door' ? 'grn' : 'ink'}
            size="sm"
            onClick={() => setArmedKind((k) => (k === 'door' ? null : 'door'))}
            title="Place a door — click a wall for a perimeter door, or inside the plot for an internal door. Both get their 32-pada reading; set the door's Type after placing."
          >
            {armedKind === 'door' ? '● Click the plan to place a door…' : '+ Add door'}
          </Btn3>
          {armedKind && (
            <button
              type="button"
              onClick={() => setArmedKind(null)}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--ink-mute)',
                fontSize: 12,
                fontFamily: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              cancel
            </button>
          )}
        </div>
      )}

      {/* Slice 5 — AUO one-shot ADD. In AUO mode, "+ Add AUO" arms placement; the
          next click INSIDE the plot drops a fixture whose 16-zone is derived from
          the grid. Name the fixture + confirm/reject it in the review panel below
          the canvas. */}
      {!displayOnly && onAddAuo && activeMode === 'auo' && (
        <div
          style={{
            display: 'inline-flex',
            gap: 8,
            alignItems: 'center',
            flexWrap: 'nowrap',
            flex: '0 0 auto',
          }}
        >
          <Btn3
            intent={armedKind === 'auo' ? 'grn' : 'ink'}
            size="sm"
            onClick={() => setArmedKind((k) => (k === 'auo' ? null : 'auo'))}
            title="Arm an AUO — then click INSIDE the plot to drop a fixture; its zone is read off the grid"
          >
            {armedKind === 'auo' ? '● Click inside the plot…' : '+ Add AUO'}
          </Btn3>
          {armedKind === 'auo' && (
            <button
              type="button"
              onClick={() => setArmedKind(null)}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'var(--ink-mute)',
                fontSize: 12,
                fontFamily: 'inherit',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              cancel
            </button>
          )}
        </div>
        )}
        </div>
      )}

      {/* the stage: image layer + SVG overlay */}
      {renderStage(
      <div
        className="evalws-plan-stage"
        style={{
          position: 'relative',
          width: '100%',
          // #1 AUTO-FIT — cap by the MEASURED available pane height (fitH); width
          // follows the aspect (height × w/h) so the WHOLE plan fits with no clip
          // and NO distortion (the aspect-ratio box keeps the true proportions).
          // Falls back to 900px until the first measure (resolved before paint via
          // useLayoutEffect, so no overflow flash).
          maxWidth:
            enableZoom && fitH != null
              ? `min(100%, ${Math.round(fitH * (view.w / view.h))}px)`
              : 900,
          maxHeight: enableZoom && fitH != null ? `${fitH}px` : undefined,
          border: '1px solid var(--rule-strong)',
          borderRadius: 'var(--r-sm)',
          overflow: 'hidden',
          background: '#fff',
          // keep the aspect ratio of the surface so the SVG sits exactly over it.
          // tracks the (possibly margin-expanded) overlay viewBox so the plan
          // never distorts when the outside labels/strip widen the view.
          aspectRatio: `${view.w} / ${view.h}`,
          // crosshair while tracing; in entrance / AUO mode a 'cell' cursor only
          // while a placement is armed (else 'default' so the mode reads as
          // select/adjust, not "click=add").
          cursor: displayOnly
            ? 'default'
            : activeMode === 'trace'
              ? 'crosshair'
              : armedKind
                ? 'cell'
                : 'default',
        }}
      >
        {!floorplan?.downloadURL && !displayOnly && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--ink-mute)',
              fontSize: 12.5,
              textAlign: 'center',
              padding: 16,
            }}
          >
            {uploading
              ? 'Uploading…'
              : 'Upload a floor plan, then click to trace the outline. You can also trace on this blank surface.'}
          </div>
        )}

        <svg
          ref={svgRef}
          viewBox={view.box}
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          onClick={displayOnly ? undefined : onCanvasClick}
          onPointerDown={displayOnly ? undefined : onCanvasPointerDown}
          onPointerMove={displayOnly ? undefined : onPointerMove}
          onPointerUp={displayOnly ? undefined : onPointerUp}
        >
          {/* Floor plan rendered INSIDE the SVG (image-pixel space 0..W/0..H) so
              the margin-expanded overlay viewBox can add space around it without
              distorting the plan — the <image> stays pinned to its pixel rect. */}
          {floorplan?.downloadURL && (
            <image
              href={floorplan.downloadURL}
              x={0}
              y={0}
              width={W}
              height={H}
              preserveAspectRatio="none"
              // BUG 1: never steal a click meant for a vertex / the canvas.
              pointerEvents="none"
              style={{ userSelect: 'none' }}
            />
          )}
          {/* read-only CAD GRID OVERLAY (locked v7 spec — pixel-faithful port).
              Clean interior (NO translucent zone fills); zone colour strip JUST
              OUTSIDE the red boundary; 32 dividers (zone=darker, pada=lighter);
              two radial label rings (pada code + zone name) anchored to the
              FARTHEST boundary hit; bracket ticks. All hairline (non-scaling
              stroke) over the floor plan, which reads through cleanly. */}
          {overlay && showGrid && (
            <g pointerEvents="none">
              {/* zone colour strip (edge-parallel, just outside) */}
              {overlay.strip.map((q, i) => (
                <path
                  key={`zs${i}`}
                  d={
                    q.poly
                      .map((p, j) => (j ? 'L' : 'M') + p.x.toFixed(1) + ' ' + p.y.toFixed(1))
                      .join(' ') + 'Z'
                  }
                  fill={q.col}
                  fillOpacity={0.6}
                  stroke="none"
                />
              ))}
              {/* 32 interior dividers: zone (even idx) darker #555, pada lighter #aeb3ba */}
              {overlay.L.map((p, idx) => {
                const i = idx + 1;
                const isZ = i % 2 === 0;
                return (
                  <line
                    key={`d${i}`}
                    x1={overlay.C.x}
                    y1={overlay.C.y}
                    x2={p.x}
                    y2={p.y}
                    stroke={isZ ? '#555' : '#aeb3ba'}
                    strokeWidth={isZ ? 1.3 : 0.5}
                    strokeOpacity={isZ ? 0.7 : 0.5}
                    vectorEffect="non-scaling-stroke"
                  />
                );
              })}
              {/* marked boundary = RED */}
              <path
                d={
                  overlay.B.map(
                    (p, j) => (j ? 'L' : 'M') + p.x.toFixed(1) + ' ' + p.y.toFixed(1),
                  ).join(' ') + 'Z'
                }
                fill="none"
                stroke="#d11"
                strokeWidth={1.8}
                vectorEffect="non-scaling-stroke"
              />
              {/* bracket ticks beyond the strip */}
              {overlay.ticks.map((t) => (
                <line
                  key={t.key}
                  x1={t.x1}
                  y1={t.y1}
                  x2={t.x2}
                  y2={t.y2}
                  stroke={t.isZone ? '#555' : '#aeb3ba'}
                  strokeWidth={t.isZone ? 1.2 : 0.5}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {/* vertex + ticks (red), centre (orange) */}
              {overlay.B.map((p, i) => (
                <g
                  key={`vx${i}`}
                  stroke="#d11"
                  strokeWidth={1.2}
                  strokeOpacity={0.6}
                  vectorEffect="non-scaling-stroke"
                >
                  <line x1={p.x - 8} y1={p.y} x2={p.x + 8} y2={p.y} />
                  <line x1={p.x} y1={p.y - 8} x2={p.x} y2={p.y + 8} />
                </g>
              ))}
              <g stroke="#b45309" strokeWidth={1.5} vectorEffect="non-scaling-stroke">
                <line x1={overlay.C.x - 12} y1={overlay.C.y} x2={overlay.C.x + 12} y2={overlay.C.y} />
                <line x1={overlay.C.x} y1={overlay.C.y - 12} x2={overlay.C.x} y2={overlay.C.y + 12} />
              </g>
              {/* inner ring: entrance-pada code, coloured text, radial */}
              {overlay.padaLabels.map((l) => (
                <text
                  key={l.key}
                  x={l.x.toFixed(1)}
                  y={l.y.toFixed(1)}
                  transform={`rotate(${l.rotate.toFixed(1)} ${l.x.toFixed(1)} ${l.y.toFixed(1)})`}
                  fontFamily='"Courier New",monospace'
                  fontSize={overlay.metrics.PAD_FS}
                  fontWeight={700}
                  fill={l.col}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {l.text}
                </text>
              ))}
              {/* outer ring: zone name, coloured text, radial */}
              {overlay.zoneLabels.map((l) => (
                <text
                  key={l.key}
                  x={l.x.toFixed(1)}
                  y={l.y.toFixed(1)}
                  transform={`rotate(${l.rotate.toFixed(1)} ${l.x.toFixed(1)} ${l.y.toFixed(1)})`}
                  fontFamily='"Courier New",monospace'
                  fontSize={overlay.metrics.ZON_FS}
                  fontWeight={700}
                  fill={l.col}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {l.text}
                </text>
              ))}
            </g>
          )}

          {/* the traced polygon */}
          {pts.length >= 2 && (
            <polygon
              points={polyPoints}
              // LOCKED SPEC (reference_grid_cad_overlay_spec): the plot interior
              // stays CLEAN — NO translucent zone/boundary fill — so the floor
              // plan beneath reads crisply. Only the boundary STROKE is drawn.
              // (v7 canonical: boundary <path fill="none">.) Was a stale amber
              // translucent interior wash on close — a spec violation; removed.
              fill="none"
              stroke="#b45309"
              strokeWidth={Math.max(W, H) / 350}
              strokeDasharray={closed ? '0' : `${Math.max(W, H) / 90}`}
              // BUG 1: the outline is decorative — clicks must pass THROUGH to
              // the canvas (add a point) or to a vertex handle.
              pointerEvents="none"
            />
          )}
          {/* draggable vertex handles (only interactive in trace mode).
              ITEM 3: small + semi-transparent so the plan reads through; a
              larger INVISIBLE circle keeps the hit-target comfortable. */}
          {pts.map((p, i) => {
            const traceOn = activeMode === 'trace' && !displayOnly;
            // the FIRST vertex while still tracing for the FIRST time (open,
            // never closed, not editing a reopened shape) with ≥3 points is the
            // close target — hint it (pointer cursor + a larger ring). When
            // EDITING a reopened boundary it is a normal draggable vertex (no
            // close hint), matching the first-point edit fix.
            const closeTarget =
              traceOn && !closed && !editing && i === 0 && pts.length >= 3;
            return (
              <g key={`v${i}`}>
                {/* transparent hit-target (bigger than the visible dot) */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={Math.max(W, H) / 70}
                  fill="transparent"
                  style={{ cursor: traceOn ? (closeTarget ? 'pointer' : 'grab') : 'default' }}
                  pointerEvents={traceOn ? 'auto' : 'none'}
                  onPointerDown={(e) => onVertexDown(i, e)}
                >
                  {closeTarget && <title>Click to close the boundary</title>}
                </circle>
                {/* a halo ring around the first vertex when it is the close target */}
                {closeTarget && (
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={Math.max(W, H) / 80}
                    fill="none"
                    stroke="#15803d"
                    strokeOpacity={0.9}
                    strokeWidth={Math.max(W, H) / 400}
                    pointerEvents="none"
                  />
                )}
                {/* small semi-transparent visible dot */}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={Math.max(W, H) / 150}
                  fill={i === 0 ? '#b45309' : '#fff'}
                  fillOpacity={0.5}
                  stroke="#b45309"
                  strokeOpacity={0.85}
                  strokeWidth={Math.max(W, H) / 450}
                  pointerEvents="none"
                />
              </g>
            );
          })}

          {/* DOOR MARKERS — door = width-RECTANGLE on the wall (Stage 3b).
              Each door is drawn as a teal band straddling its boundary segment,
              from the door's start end-point to its end end-point (centre ±
              width/2 walked along the perimeter, spilling across corners). Drag
              the BODY to slide it along the wall; drag the WIDTH HANDLE (the
              perpendicular nub) to widen/narrow. The covered-pada codes
              (e.g. "N3–N4") render beside it = the live trust signal. Principal
              door is filled + ★. */}
          {grid &&
            isValidBoundary(pts) &&
            entranceMarks
              .filter((m) => m.placement !== 'internal')
              .map((m) => {
              const span = doorSpan(
                pts,
                m.edge_index,
                m.parametric_t,
                m.width_image_px,
              );
              if (!span) return null;
              const interactive = activeMode === 'entrance' && !displayOnly;
              // ITEM 3: a LIGHT, thin, semi-transparent band — not a heavy glyph.
              const band = Math.max(W, H) / 130; // half-thickness (slimmer)
              const fs = Math.max(W, H) / 55;
              const gripR = Math.max(W, H) / 120; // small visible grip
              const hitR = Math.max(W, H) / 65; // larger invisible hit-target
              // wall direction (start → end) and its perpendicular (band thickness).
              const wx = span.end.x - span.start.x;
              const wy = span.end.y - span.start.y;
              const wlen = Math.hypot(wx, wy) || 1;
              const nx = (-wy / wlen) * band; // perpendicular, scaled to half-band
              const ny = (wx / wlen) * band;
              // the 4 corners of the door rectangle (band straddling the wall).
              const corners = [
                `${span.start.x + nx},${span.start.y + ny}`,
                `${span.end.x + nx},${span.end.y + ny}`,
                `${span.end.x - nx},${span.end.y - ny}`,
                `${span.start.x - nx},${span.start.y - ny}`,
              ].join(' ');
              const padaLabel =
                m.padas.length === 0
                  ? '?'
                  : m.padas.length === 1
                    ? m.padas[0]
                    : `${m.padas[0]}–${m.padas[m.padas.length - 1]}`;
              return (
                <g key={m.id}>
                  {/* the door RECTANGLE band — drag to slide along the wall.
                      ITEM 3: low opacity + thin stroke so the plan reads through. */}
                  <polygon
                    points={corners}
                    fill={m.isPrincipal ? '#0d9488' : '#14b8a6'}
                    fillOpacity={m.isPrincipal ? 0.4 : 0.28}
                    stroke="#0f766e"
                    strokeOpacity={0.7}
                    strokeWidth={Math.max(W, H) / 500}
                    style={{ cursor: interactive ? 'grab' : 'default' }}
                    pointerEvents={interactive ? 'auto' : 'none'}
                    onPointerDown={(e) => interactive && onEntranceDown(m.id, e)}
                  />
                  {/* ITEM 4: two END grips on the wall — drag to set the span.
                      Each has a larger transparent hit-target + a small visible
                      semi-transparent dot. Only in entrance mode. */}
                  {interactive &&
                    (['start', 'end'] as const).map((which) => {
                      const e = which === 'start' ? span.start : span.end;
                      return (
                        <g key={which}>
                          <circle
                            cx={e.x}
                            cy={e.y}
                            r={hitR}
                            fill="transparent"
                            style={{ cursor: 'grab' }}
                            onPointerDown={(ev) => onEndGripDown(m.id, which, ev)}
                          >
                            <title>Drag to set this end of the door</title>
                          </circle>
                          <circle
                            cx={e.x}
                            cy={e.y}
                            r={gripR}
                            fill="#fff"
                            fillOpacity={0.6}
                            stroke="#0f766e"
                            strokeOpacity={0.9}
                            strokeWidth={Math.max(W, H) / 500}
                            pointerEvents="none"
                          />
                        </g>
                      );
                    })}
                  {/* the derived covered-pada label beside the door (★ marks the
                      principal — light, inline, not a heavy box). */}
                  <text
                    x={m.centre.x - nx * 3}
                    y={m.centre.y - ny * 3}
                    fontSize={fs}
                    fontWeight={700}
                    fill="#0f766e"
                    fillOpacity={0.95}
                    textAnchor="middle"
                    dominantBaseline="central"
                    pointerEvents="none"
                    style={{ paintOrder: 'stroke' }}
                    stroke="#fff"
                    strokeWidth={fs / 5}
                  >
                    {m.isPrincipal ? `★ ${padaLabel}` : padaLabel}
                  </text>
                  {/* #3 — unified number badge: the door's index in the disposition
                      (doors take the first numbers), so the map == the list. */}
                  <circle cx={m.centre.x} cy={m.centre.y} r={fs * 0.6} fill="#0f766e" pointerEvents="none" />
                  <text
                    x={m.centre.x}
                    y={m.centre.y}
                    fontSize={fs * 0.78}
                    fontWeight={700}
                    fill="#fff"
                    textAnchor="middle"
                    dominantBaseline="central"
                    pointerEvents="none"
                  >
                    {entranceMarks.findIndex((x) => x.id === m.id) + 1}
                  </text>
                </g>
              );
            })}

          {/* INTERNAL DOOR MARKERS — an entry off a staircase / lift lobby that
              sits INSIDE the footprint. The SAME rectangular width-door as the
              perimeter doors (band + two draggable end grips), just placed FREELY
              (not snapped to a wall) and drawn in AMBER to distinguish it. Drag
              the BODY to move it; drag an END grip to rotate / resize. Its
              covered padas are read by DIRECTION (every sector its span subtends)
              and shown beside it — multiple padas list as "E4–E5". Principal is
              filled darker + ★. */}
          {grid &&
            isValidBoundary(pts) &&
            entranceMarks
              .filter((m) => m.placement === 'internal' && m.a && m.b)
              .map((m) => {
                const a = m.a as Pt;
                const b = m.b as Pt;
                const interactive = activeMode === 'entrance' && !displayOnly;
                const band = Math.max(W, H) / 130; // half-thickness (matches perimeter)
                const fs = Math.max(W, H) / 55;
                const gripR = Math.max(W, H) / 120;
                const hitR = Math.max(W, H) / 65;
                // door direction (a → b) + its perpendicular (band thickness).
                const wx = b.x - a.x;
                const wy = b.y - a.y;
                const wlen = Math.hypot(wx, wy) || 1;
                const nx = (-wy / wlen) * band;
                const ny = (wx / wlen) * band;
                const corners = [
                  `${a.x + nx},${a.y + ny}`,
                  `${b.x + nx},${b.y + ny}`,
                  `${b.x - nx},${b.y - ny}`,
                  `${a.x - nx},${a.y - ny}`,
                ].join(' ');
                const padaLabel =
                  m.padas.length === 0
                    ? '?'
                    : m.padas.length === 1
                      ? m.padas[0]
                      : `${m.padas[0]}–${m.padas[m.padas.length - 1]}`;
                return (
                  <g key={m.id}>
                    {/* the door RECTANGLE band — drag the body to move it freely. */}
                    <polygon
                      points={corners}
                      fill={m.isPrincipal ? '#d97706' : '#f59e0b'}
                      fillOpacity={m.isPrincipal ? 0.4 : 0.28}
                      stroke="#b45309"
                      strokeOpacity={0.8}
                      strokeWidth={Math.max(W, H) / 500}
                      style={{ cursor: interactive ? 'grab' : 'default' }}
                      pointerEvents={interactive ? 'auto' : 'none'}
                      onPointerDown={(e) => interactive && onEntranceDown(m.id, e)}
                    >
                      <title>Internal door — drag to move; drag the ends to rotate / resize</title>
                    </polygon>
                    {/* two END grips — drag to rotate / resize the door freely
                        (NOT snapped to a wall). Only in entrance mode. */}
                    {interactive &&
                      (['start', 'end'] as const).map((which) => {
                        const e = which === 'start' ? a : b;
                        return (
                          <g key={which}>
                            <circle
                              cx={e.x}
                              cy={e.y}
                              r={hitR}
                              fill="transparent"
                              style={{ cursor: 'grab' }}
                              onPointerDown={(ev) => onEndGripDown(m.id, which, ev)}
                            >
                              <title>Drag to set this end of the internal door</title>
                            </circle>
                            <circle
                              cx={e.x}
                              cy={e.y}
                              r={gripR}
                              fill="#fff"
                              fillOpacity={0.6}
                              stroke="#b45309"
                              strokeOpacity={0.9}
                              strokeWidth={Math.max(W, H) / 500}
                              pointerEvents="none"
                            />
                          </g>
                        );
                      })}
                    {/* the direction-pada label beside the door (★ = principal) */}
                    <text
                      x={m.centre.x - nx * 3}
                      y={m.centre.y - ny * 3}
                      fontSize={fs}
                      fontWeight={700}
                      fill="#b45309"
                      fillOpacity={0.95}
                      textAnchor="middle"
                      dominantBaseline="central"
                      pointerEvents="none"
                      style={{ paintOrder: 'stroke' }}
                      stroke="#fff"
                      strokeWidth={fs / 5}
                    >
                      {m.isPrincipal ? `★ ${padaLabel}` : padaLabel}
                    </text>
                  </g>
                );
              })}

          {/* ── AUO markers (Slice 5 REDESIGN): each AUO is a SHAPE (rect with
              rotation, or circle) that can straddle MULTIPLE 16-zones. We draw the
              shape outline + ONE numbered badge at its centre — the number ties to
              its review card (no long text on the map → declutters 10-15 items).
              Colour by status: amber = proposed, teal = confirmed, grey =
              rejected. Selected = a thicker ring + a corner resize grip. Draggable
              + resizable only in AUO mode. ── */}
          {grid &&
            auoMarks.map((m, idx) => {
              const interactive = activeMode === 'auo' && !displayOnly;
              const selected = selectedAuoId === m.id;
              // #3 — ONE unified sequence: doors take the first numbers (1..d), AUOs
              // continue after them, so the map badge == the AUO disposition row #.
              const num = entranceMarks.length + idx + 1;
              const reg = regionForMark(m);
              const badgeR = Math.max(W, H) / 42;
              const fs = badgeR * 1.05;
              const sw = Math.max(W, H) / 520;
              const colour =
                m.status === 'confirmed'
                  ? '#0d9488'
                  : m.status === 'rejected'
                    ? '#9ca3af'
                    : '#d97706'; // proposed
              const shapeStroke = selected ? sw * 3 : sw * 1.6;
              const shapeFillOp = m.status === 'rejected' ? 0.06 : selected ? 0.16 : 0.1;
              // corner grip (resize) — top-right corner of the rect, or the
              // right-most point of the circle.
              let grip: Pt;
              if (reg.kind === 'circle') grip = { x: reg.cx + reg.r, y: reg.cy };
              else grip = rectCorners(reg)[1]; // top-right corner
              return (
                <g key={m.id}>
                  {/* the shape outline (the AUO footprint) */}
                  {reg.kind === 'circle' ? (
                    <circle
                      cx={reg.cx}
                      cy={reg.cy}
                      r={reg.r}
                      fill={colour}
                      fillOpacity={shapeFillOp}
                      stroke={colour}
                      strokeWidth={shapeStroke}
                      strokeDasharray={selected ? undefined : `${sw * 6} ${sw * 4}`}
                      style={{ cursor: interactive ? 'grab' : 'default' }}
                      onPointerDown={(e) => interactive && onAuoDown(m.id, e)}
                    >
                      <title>AUO {num} — drag to move; corner grip resizes; zones re-derive</title>
                    </circle>
                  ) : (
                    <polygon
                      points={rectCorners(reg).map((c) => `${c.x},${c.y}`).join(' ')}
                      fill={colour}
                      fillOpacity={shapeFillOp}
                      stroke={colour}
                      strokeWidth={shapeStroke}
                      strokeDasharray={selected ? undefined : `${sw * 6} ${sw * 4}`}
                      style={{ cursor: interactive ? 'grab' : 'default' }}
                      onPointerDown={(e) => interactive && onAuoDown(m.id, e)}
                    >
                      <title>AUO {num} — drag to move; corner grip resizes; zones re-derive</title>
                    </polygon>
                  )}

                  {/* selection ring behind the numbered badge */}
                  {selected && (
                    <circle
                      cx={reg.cx}
                      cy={reg.cy}
                      r={badgeR * 1.5}
                      fill="none"
                      stroke={colour}
                      strokeWidth={sw * 2}
                      pointerEvents="none"
                    />
                  )}

                  {/* the NUMBERED badge at the centre (the review-card reference) */}
                  <circle
                    cx={reg.cx}
                    cy={reg.cy}
                    r={badgeR}
                    fill={colour}
                    fillOpacity={m.status === 'rejected' ? 0.5 : 0.95}
                    stroke="#fff"
                    strokeWidth={sw * 1.5}
                    style={{ cursor: interactive ? 'grab' : displayOnly ? 'default' : 'pointer' }}
                    onPointerDown={(e) => (interactive ? onAuoDown(m.id, e) : undefined)}
                    onClick={(e) => {
                      if (!interactive && !displayOnly) {
                        e.stopPropagation();
                        onSelectAuo?.(m.id);
                      }
                    }}
                  >
                    <title>AUO {num}{m.name ? ` — ${m.name}` : ''}</title>
                  </circle>
                  <text
                    x={reg.cx}
                    y={reg.cy}
                    fontSize={fs}
                    fontWeight={800}
                    fill="#fff"
                    textAnchor="middle"
                    dominantBaseline="central"
                    pointerEvents="none"
                  >
                    {num}
                  </text>

                  {/* corner RESIZE grip (AUO mode + selected only). Grip = the
                      top-right corner (index 1 of rectCorners), so the OPPOSITE
                      (bottom-left, index 3) stays pinned during the drag. */}
                  {interactive && selected && (onResizeAuoAnchored || onResizeAuo) && (
                    <circle
                      cx={grip.x}
                      cy={grip.y}
                      r={badgeR * 0.55}
                      fill="#fff"
                      stroke={colour}
                      strokeWidth={sw * 2}
                      style={{ cursor: 'nwse-resize' }}
                      onPointerDown={(e) => onAuoResizeDown(m.id, 1, e)}
                    >
                      <title>Resize — the opposite corner stays pinned; covered zones re-derive</title>
                    </circle>
                  )}

                  {/* a small on-map remove ✕ (AUO mode + selected only) */}
                  {interactive && selected && onRemoveAuo && (
                    <text
                      x={reg.cx - badgeR * 2}
                      y={reg.cy - badgeR * 2}
                      fontSize={fs}
                      fontWeight={700}
                      fill="#dc2626"
                      textAnchor="middle"
                      dominantBaseline="central"
                      style={{ cursor: 'pointer', paintOrder: 'stroke' }}
                      stroke="#fff"
                      strokeWidth={fs / 5}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        swallowNextClick.current = true;
                        onRemoveAuo(m.id);
                      }}
                    >
                      ✕
                    </text>
                  )}
                </g>
              );
            })}

          {/* MARKUPS (#2) — annotations over the plan, in image-pixel space so they
              pan/zoom + align with it. Numbered to match the Markups disposition;
              selectable (click), movable (drag body), resizable (rect grip), and
              deletable (from the list). Drawn LAST so they sit above the plan. */}
          {(markups ?? []).map((mk, i) => {
            const interactive = activeMode === 'markup' && !displayOnly;
            const sel = selectedMarkupId === mk.id;
            const u = Math.max(W, H) / 46; // glyph unit
            const sw = Math.max(W, H) / 480;
            const COL = '#6d28d9'; // violet = annotation
            const grab = interactive ? 'grab' : 'default';
            const bodyDown = (e: React.PointerEvent) => interactive && onMarkupDown(mk.id, e);
            // a generous transparent hit area + the selection ring bbox
            let bx = mk.x, by = mk.y, bw = 0, bh = 0;
            if (isRectMarkup(mk.type)) { bw = mk.w ?? u * 2; bh = mk.h ?? u; }
            else if (mk.type === 'arrow') { bx = Math.min(mk.x, mk.x2 ?? mk.x); by = Math.min(mk.y, mk.y2 ?? mk.y); bw = Math.abs((mk.x2 ?? mk.x) - mk.x); bh = Math.abs((mk.y2 ?? mk.y) - mk.y); }
            else { bx = mk.x - u; by = mk.y - u; bw = u * 2; bh = u * 2; }
            return (
              <g key={mk.id}>
                {/* selection ring */}
                {sel && (
                  <rect x={bx - u * 0.3} y={by - u * 0.3} width={bw + u * 0.6} height={bh + u * 0.6}
                    fill="none" stroke={COL} strokeWidth={sw * 1.6} strokeDasharray={`${sw * 5} ${sw * 4}`} rx={u * 0.3} pointerEvents="none" />
                )}

                {/* the glyph / shape (violet) */}
                {mk.type === 'tick' && (
                  <path d={`M ${mk.x - u * 0.5} ${mk.y} l ${u * 0.35} ${u * 0.45} l ${u * 0.7} ${-u * 0.9}`} fill="none" stroke="#15803d" strokeWidth={sw * 3} strokeLinecap="round" strokeLinejoin="round" style={{ cursor: grab }} onPointerDown={bodyDown} />
                )}
                {mk.type === 'cross' && (
                  <path d={`M ${mk.x - u * 0.5} ${mk.y - u * 0.5} l ${u} ${u} M ${mk.x + u * 0.5} ${mk.y - u * 0.5} l ${-u} ${u}`} fill="none" stroke="#b91c1c" strokeWidth={sw * 3} strokeLinecap="round" style={{ cursor: grab }} onPointerDown={bodyDown} />
                )}
                {mk.type === 'question' && (
                  <g style={{ cursor: grab }} onPointerDown={bodyDown}>
                    <circle cx={mk.x} cy={mk.y} r={u * 0.7} fill="#fff" stroke="#b45309" strokeWidth={sw * 1.6} />
                    <text x={mk.x} y={mk.y} fontSize={u} fontWeight={700} fill="#92400e" textAnchor="middle" dominantBaseline="central">?</text>
                  </g>
                )}
                {mk.type === 'text' && (
                  <text x={mk.x} y={mk.y} fontSize={u} fontWeight={700} fill={COL} dominantBaseline="central" style={{ cursor: grab }} onPointerDown={bodyDown}>{mk.note || 'Note'}</text>
                )}
                {mk.type === 'arrow' && (
                  <g style={{ cursor: grab }} onPointerDown={bodyDown}>
                    <line x1={mk.x} y1={mk.y} x2={mk.x2 ?? mk.x} y2={mk.y2 ?? mk.y} stroke="#1a1a1a" strokeWidth={sw * 2.4} strokeLinecap="round" />
                    {(() => {
                      const ex = mk.x2 ?? mk.x, ey = mk.y2 ?? mk.y;
                      const ang = Math.atan2(ey - mk.y, ex - mk.x);
                      const ah = u * 0.5;
                      const a1 = ang + Math.PI * 0.83, a2 = ang - Math.PI * 0.83;
                      return <path d={`M ${ex} ${ey} L ${ex + Math.cos(a1) * ah} ${ey + Math.sin(a1) * ah} M ${ex} ${ey} L ${ex + Math.cos(a2) * ah} ${ey + Math.sin(a2) * ah}`} stroke="#1a1a1a" strokeWidth={sw * 2.4} strokeLinecap="round" fill="none" />;
                    })()}
                  </g>
                )}
                {mk.type === 'cloud' && (
                  <path d={scallopRectPath(mk.x, mk.y, mk.w ?? u * 2, mk.h ?? u, u * 0.34)} fill="#fef2f2" fillOpacity={0.25} stroke="#dc2626" strokeWidth={sw * 1.8} style={{ cursor: grab }} onPointerDown={bodyDown} />
                )}
                {mk.type === 'mirror' && (
                  <g style={{ cursor: grab }} onPointerDown={bodyDown}>
                    <rect x={mk.x} y={mk.y} width={mk.w ?? u * 2} height={mk.h ?? u} fill="#eff6ff" fillOpacity={0.22} stroke="#2563eb" strokeWidth={sw * 1.8} strokeDasharray={`${sw * 5} ${sw * 3}`} />
                    <line x1={mk.x + (mk.w ?? u * 2) / 2} y1={mk.y} x2={mk.x + (mk.w ?? u * 2) / 2} y2={mk.y + (mk.h ?? u)} stroke="#2563eb" strokeWidth={sw * 1.4} strokeDasharray={`${sw * 3} ${sw * 3}`} />
                  </g>
                )}

                {/* number badge — the ONE unified sequence: doors take the first
                    numbers (1..d), AUOs continue after them, markups continue
                    after the AUOs. So the map pin == the row # in the unified
                    list (was `i + 1`, a per-array index that mis-numbered every
                    markup pin — fixed 2026-07-01, mirrors the AUO badge above). */}
                <circle cx={bx} cy={by} r={u * 0.62} fill={COL} pointerEvents="none" />
                <text x={bx} y={by} fontSize={u * 0.8} fontWeight={700} fill="#fff" textAnchor="middle" dominantBaseline="central" pointerEvents="none">{entranceMarks.length + auoMarks.length + i + 1}</text>

                {/* resize grip (rect markups, when selected + interactive) */}
                {sel && interactive && isRectMarkup(mk.type) && (
                  <circle cx={mk.x + (mk.w ?? u * 2)} cy={mk.y + (mk.h ?? u)} r={u * 0.42} fill="#fff" stroke={COL} strokeWidth={sw * 1.6} style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => onMarkupResizeDown(mk.id, e)} />
                )}
                {/* arrow end grip (when selected) */}
                {sel && interactive && mk.type === 'arrow' && (
                  <circle cx={mk.x2 ?? mk.x} cy={mk.y2 ?? mk.y} r={u * 0.42} fill="#fff" stroke={COL} strokeWidth={sw * 1.6} style={{ cursor: 'move' }} onPointerDown={(e) => onMarkupResizeDown(mk.id, e)} />
                )}
                {/* delete grip (when selected) — also deletable from the Markups list */}
                {sel && interactive && (
                  <g style={{ cursor: 'pointer' }} onPointerDown={(e) => { e.stopPropagation(); onRemoveMarkup?.(mk.id); }}>
                    <circle cx={bx + bw + u * 0.3} cy={by - u * 0.3} r={u * 0.44} fill="#fff" stroke="#b91c1c" strokeWidth={sw * 1.6} />
                    <path d={`M ${bx + bw + u * 0.3 - u * 0.22} ${by - u * 0.3 - u * 0.22} l ${u * 0.44} ${u * 0.44} M ${bx + bw + u * 0.3 + u * 0.22} ${by - u * 0.3 - u * 0.22} l ${-u * 0.44} ${u * 0.44}`} stroke="#b91c1c" strokeWidth={sw * 1.9} strokeLinecap="round" />
                  </g>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      )}

      {/* status line — suppressed in read-only DISPLAY mode (the preview is silent) */}
      {!displayOnly && (
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6 }}>
        {activeMode === 'auo'
          ? `${
              armedKind === 'auo'
                ? 'Click inside the plot to place the AUO — its zone is read off the grid.'
                : 'Press “+ Add AUO”, then click inside the plot. Name + confirm each fixture in the review panel below.'
            } Drag an AUO to move it (its zone re-derives). ${
              auoMarks.length
            } AUO${auoMarks.length === 1 ? '' : 's'} marked.`
          : activeMode === 'entrance'
          ? `${
              armedKind === 'perimeter'
                ? 'Click a wall to place the door.'
                : armedKind === 'internal'
                  ? 'Click inside the plot to place the internal door.'
                  : 'Add a wall door (“+ Add door”) or an internal door off a staircase / lift (“+ Add internal door”).'
            } Drag a door to move it; drag its end grips to set the span — a wall door rides the wall, an internal door rotates / resizes freely and reads its padas by direction. ${
              entranceMarks.length
            } door${entranceMarks.length === 1 ? '' : 's'} marked.`
          : pts.length === 0
            ? 'Click around the outer wall of the unit to drop boundary points. Drag a point to adjust.'
            : closed
              ? `Boundary closed · ${pts.length} points · grid generated${
                  north != null ? ` · north ${north}°` : ' · set north for a true-aligned grid'
                }.`
              : pts.length < 3
                ? `${pts.length} point${pts.length === 1 ? '' : 's'} — add at least ${
                    3 - pts.length
                  } more, then close the boundary to generate the grid.`
                : `${pts.length} points — keep clicking to add corners. When the outline is complete, click the first point (or “Close boundary & generate grid”).`}
      </p>
      )}

      {/* DOOR LIST — one row per door: the derived COVERED padas (live confirm),
          a 5-type dropdown, an optional name, principal radio, remove. Width is
          set on the drawing via the door's two end grips (item 4). "Door 1 ·
          N3–N4 · main_door" is the trust signal that replaces blind typing — a
          door over two padas lists both. SUPPRESSED in read-only display mode. */}
      {!displayOnly && onAddEntrance && entranceMarks.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <p
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--ink)',
              margin: '0 0 6px',
            }}
          >
            Doors on this floor ({entranceMarks.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {entranceMarks.map((m, mi) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  padding: '7px 9px',
                  background: m.isPrincipal ? 'var(--inset, #f6f8f7)' : '#fff',
                }}
              >
                {/* door number + placement tag (internal vs wall) */}
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: 'var(--ink-mute)',
                    minWidth: 48,
                  }}
                >
                  {m.placement === 'internal' ? 'Internal' : 'Door'} {mi + 1}
                </span>
                {/* the DERIVED pada(s) — live confirmation, never typed. A
                    perimeter door over two padas lists both (owner rule); an
                    internal door shows its single direction-pada. */}
                <span
                  title={
                    m.placement === 'internal'
                      ? "This internal door's pada, read by direction from the centre"
                      : "Every pada this door's width covers, read off the grid"
                  }
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: m.padas.length ? '#0f766e' : 'var(--crit, #dc2626)',
                    minWidth: 48,
                  }}
                >
                  {m.padas.length ? m.padas.join(', ') : '— off grid'}
                </span>
                {/* type dropdown — the vocab depends on placement (a perimeter
                    door shows the 5 perimeter types; an internal door the
                    staircase / internal / lift set). */}
                <select
                  value={m.type}
                  onChange={(e) => onSetEntranceType?.(m.id, e.target.value)}
                  style={{
                    border: '1px solid var(--rule-strong)',
                    borderRadius: 'var(--r-sm)',
                    padding: '5px 7px',
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                  }}
                  aria-label="Entrance type"
                >
                  {(m.placement === 'internal'
                    ? INTERNAL_ENTRANCE_TYPES
                    : PERIMETER_ENTRANCE_TYPES
                  ).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <input
                  value={m.name || ''}
                  onChange={(e) => onSetEntranceName?.(m.id, e.target.value)}
                  placeholder="name (optional)"
                  style={{
                    border: '1px solid var(--rule-strong)',
                    borderRadius: 'var(--r-sm)',
                    padding: '5px 7px',
                    fontSize: 12.5,
                    fontFamily: 'inherit',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    flex: '1 1 120px',
                    minWidth: 90,
                  }}
                />
                <label
                  style={{
                    display: 'inline-flex',
                    gap: 4,
                    alignItems: 'center',
                    fontSize: 12,
                    color: 'var(--ink)',
                    cursor: 'pointer',
                  }}
                  title="The principal / main entry of this floor (one per floor)"
                >
                  <input
                    type="radio"
                    name={`principal-${entranceMarks[0]?.id || 'x'}`}
                    checked={m.isPrincipal}
                    onChange={() => onSetPrincipal?.(m.id)}
                  />
                  main entry
                </label>
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={() => onRemoveEntrance?.(m.id)}
                  aria-label="Remove entrance"
                >
                  Remove
                </Btn3>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
